import type {
  ChatMessage,
  ChatMessageRole,
  ChatRequest,
  ChatResponse,
  ChatStreamChunk,
  ChatTool,
} from "@home-finance-manager/shared/types/chat"
import { Mistral } from "@mistralai/mistralai"

const MISTRAL_API_KEY = (import.meta as any).env.VITE_MISTRAL_API_KEY as string | undefined
const MISTRAL_MODEL =
  ((import.meta as any).env.VITE_MISTRAL_MODEL as string | undefined) || "mistral-large-latest"

export class MistralClient {
  private client: Mistral | null = null

  constructor() {
    if (MISTRAL_API_KEY) {
      this.client = new Mistral({ apiKey: MISTRAL_API_KEY })
    }
  }

  /**
   * Check if the client is configured
   */
  isConfigured(): boolean {
    return this.client !== null
  }

  /**
   * Convert chat messages to Mistral format
   */
  private convertMessages(messages: ChatMessage[]): Array<{
    role: "user" | "assistant" | "system" | "tool"
    content: string
    toolCalls?: Array<{
      id: string
      type: "function"
      function: {
        name: string
        arguments: string
      }
    }>
    toolCallId?: string
    name?: string
  }> {
    return messages.map((msg) => {
      // Tool messages should have role "tool" and include toolCallId
      if (msg.role === "tool") {
        return {
          role: "tool" as const,
          content: msg.content,
          toolCallId: msg.toolCallId,
        }
      }

      const base: any = {
        role: msg.role,
        content: msg.content,
      }

      if (msg.toolCalls && msg.toolCalls.length > 0) {
        base.toolCalls = msg.toolCalls.map((tc) => ({
          id: tc.id,
          type: "function" as const,
          function: {
            name: tc.name,
            arguments: JSON.stringify(tc.arguments),
          },
        }))
      }

      return base
    })
  }

  /**
   * Convert tools to Mistral format
   */
  private convertTools(tools: ChatTool[]): Array<{
    type: "function"
    function: {
      name: string
      description: string
      parameters: Record<string, unknown>
    }
  }> {
    return tools.map((tool) => ({
      type: "function" as const,
      function: {
        name: tool.function.name,
        description: tool.function.description,
        parameters: tool.function.parameters,
      },
    }))
  }

  /**
   * Send a chat request (non-streaming)
   */
  async chat(request: ChatRequest): Promise<ChatResponse> {
    if (!this.client) {
      throw new Error(
        "Mistral client is not configured. Set VITE_MISTRAL_API_KEY environment variable."
      )
    }

    const response = await this.client.chat.complete({
      model: MISTRAL_MODEL,
      messages: this.convertMessages(request.messages),
      tools: request.tools ? this.convertTools(request.tools) : undefined,
      toolChoice:
        request.toolChoice === "auto"
          ? "auto"
          : request.toolChoice === "none"
            ? "none"
            : request.toolChoice,
      temperature: request.temperature,
      maxTokens: request.maxTokens,
    })

    const message = response.choices[0]?.message

    if (!message) {
      throw new Error("No message in response")
    }

    return {
      id: response.id,
      message: {
        id: response.id,
        role: message.role === "assistant" ? "assistant" : "user",
        content: typeof message.content === "string" ? message.content : "",
        timestamp: new Date().toISOString(),
      },
      finishReason: response.choices[0]?.finishReason as any,
      usage: response.usage
        ? {
            promptTokens: response.usage.promptTokens || 0,
            completionTokens: response.usage.completionTokens || 0,
            totalTokens: response.usage.totalTokens || 0,
          }
        : undefined,
    }
  }

  /**
   * Send a streaming chat request
   */
  async *chatStream(request: ChatRequest): AsyncGenerator<ChatStreamChunk, void, unknown> {
    if (!this.client) {
      throw new Error(
        "Mistral client is not configured. Set VITE_MISTRAL_API_KEY environment variable."
      )
    }

    const stream = await this.client.chat.stream({
      model: MISTRAL_MODEL,
      messages: this.convertMessages(request.messages),
      tools: request.tools ? this.convertTools(request.tools) : undefined,
      toolChoice:
        request.toolChoice === "auto"
          ? "auto"
          : request.toolChoice === "none"
            ? "none"
            : request.toolChoice,
      temperature: request.temperature,
      maxTokens: request.maxTokens,
    })

    for await (const chunk of stream) {
      if (!chunk.data.choices || chunk.data.choices.length === 0) {
        continue
      }

      const delta = chunk.data.choices[0]?.delta

      if (!delta) {
        continue
      }

      const streamChunk: ChatStreamChunk = {
        id: chunk.data.id,
        delta: {
          role: delta.role as ChatMessageRole,
          content: typeof delta.content === "string" ? delta.content : undefined,
          toolCalls: delta.toolCalls?.map((tc: any) => ({
            index: tc.index || 0,
            id: tc.id || `tool-call-${tc.index || 0}`,
            name: tc.function?.name,
            arguments: tc.function?.arguments,
          })),
        },
        finishReason: chunk.data.choices[0]?.finishReason as any,
        usage: chunk.data.usage
          ? {
              promptTokens: chunk.data.usage.promptTokens || 0,
              completionTokens: chunk.data.usage.completionTokens || 0,
              totalTokens: chunk.data.usage.totalTokens || 0,
            }
          : undefined,
      }

      yield streamChunk
    }
  }
}

export const mistralClient = new MistralClient()
